import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CustomerService } from "services/CustomerService";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ApiKeySidebar from "../sidebars/ApiKeySidebar";
import { GetCustomerDashboardDto } from "services/Dtos/GetCustomerDashboardDto";
import { GetApiKeyDto } from "services/Dtos/ApiKeyDtos";
import Breadcrumb from "../common/Breadcrumb";
import CustomerOverviewTab from "./Tabs/CustomerDetails/CustomerOverviewTab";
import CustomerApiKeysTab from "./Tabs/CustomerDetails/CustomerApiKeysTab";
import CustomerAnalyticsTab from "./Tabs/CustomerDetails/CustomerAnalyticsTab";
import CustomerEditTab from "./Tabs/CustomerDetails/CustomerEditTab";

type TabType = 'overview' | 'api-keys' | 'analytics' | 'edit';
type SortField = 'id' | 'name' | 'key' | 'phone' | 'email' | 'configs';
type SortDirection = 'asc' | 'desc';

const CustomerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [customer, setCustomer] = useState<GetCustomerDashboardDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // API Keys tab state
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredApiKeys, setFilteredApiKeys] = useState<GetApiKeyDto[]>([]);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [togglingGemini, setTogglingGemini] = useState<number | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedApiKeyId, setSelectedApiKeyId] = useState<number | undefined>(undefined);


  // Edit tab state
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isEditLoading, setIsEditLoading] = useState(false);

  const fetchCustomer = async () => {
    try {
      const response = await CustomerService.customer.getCustomer(parseInt(id || ""));
      setCustomer(response.data || null);

      // Initialize form data with customer values
      if (response.data) {
        const { name, apiKeys } = response.data;
        let phone = "";
        let email = "";

        if (apiKeys && apiKeys.length > 0) {
          const apiKeyWithData = apiKeys.find((key: any) => key.phone && key.email);
          if (apiKeyWithData) {
            phone = apiKeyWithData.phone || "";
            email = apiKeyWithData.email || "";
          } else {
            const firstWithPhone = apiKeys.find((key: any) => key.phone);
            const firstWithEmail = apiKeys.find((key: any) => key.email);
            phone = firstWithPhone ? firstWithPhone.phone || "" : "";
            email = firstWithEmail ? firstWithEmail.email || "" : "";
          }
        }

        setFormData({ name, phone, email });
      }
    } catch (err) {
      setError("Failed to load customer details");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchCustomer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (customer) {
      handleSearch(searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, searchTerm, sortField, sortDirection]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    if (!customer) return;

    if (!term.trim()) {
      setFilteredApiKeys(sortApiKeys(customer.apiKeys));
      return;
    }

    const lowerTerm = term.toLowerCase();
    const filtered = customer.apiKeys.filter((apiKey) => {
      return (
        apiKey.name.toLowerCase().includes(lowerTerm) ||
        apiKey.key.toLowerCase().includes(lowerTerm) ||
        (apiKey.phone && apiKey.phone.toLowerCase().includes(lowerTerm)) ||
        (apiKey.email && apiKey.email.toLowerCase().includes(lowerTerm)) ||
        (apiKey.description && apiKey.description.toLowerCase().includes(lowerTerm))
      );
    });

    setFilteredApiKeys(sortApiKeys(filtered));
  };

  const sortApiKeys = (apiKeys: GetApiKeyDto[]): GetApiKeyDto[] => {
    return [...apiKeys].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'key':
          aValue = a.key;
          bValue = b.key;
          break;
        case 'phone':
          aValue = a.phone || '';
          bValue = b.phone || '';
          break;
        case 'email':
          aValue = a.email || '';
          bValue = b.email || '';
          break;
        case 'configs':
          aValue = a.apiKeyConfigs.length;
          bValue = b.apiKeyConfigs.length;
          break;
        default:
          aValue = a.name;
          bValue = b.name;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleToggleGemini = async (apiKeyId: number, apiKeyName: string) => {
    setTogglingGemini(apiKeyId);
    try {
      await CustomerService.apiKey.toggleGemini(apiKeyId);
      toast.success(`Gemini setting updated for ${apiKeyName}`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      fetchCustomer();
    } catch (err) {
      console.error("Failed to toggle Gemini:", err);
      toast.error("Failed to update Gemini setting", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setTogglingGemini(null);
    }
  };

  const handleCreateNew = () => {
    setSelectedApiKeyId(undefined);
    setIsSidebarOpen(true);
  };

  const handleCloseSidebar = (shouldReload: boolean) => {
    setIsSidebarOpen(false);
    setSelectedApiKeyId(undefined);
    if (shouldReload) {
      fetchCustomer();
    }
  };

  const handleSaved = () => {
    fetchCustomer();
  };

  // Edit form handlers
  const isValidUSPhoneNumber = (phone: string): boolean => {
    const phoneRegex = /^(?:\(\d{3}\)\s?\d{3}-\d{4}|\d{3}-\d{3}-\d{4}|\d{10})$/;
    return phoneRegex.test(phone);
  };

  const formatPhoneNumber = (phone: string): string => {
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length !== 10) return phone;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === "phone") {
      formattedValue = formatPhoneNumber(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => {
        const { [name]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const validateField = (name: string, value: string): string | null => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Name is required";
        return null;
      case "phone":
        if (value && !isValidUSPhoneNumber(value))
          return "Phone must be a valid US phone number";
        return null;
      case "email":
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return "Email must be valid";
        return null;
      default:
        return null;
    }
  };

  const handleEditSave = async () => {
    if (!customer) return;

    const newErrors: Record<string, string> = {};
    Object.entries(formData).forEach(([key, value]) => {
      const error = validateField(key, value);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    try {
      setIsEditLoading(true);
      const updateData = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
      };

      await CustomerService.customer.updateCustomer(parseInt(id || ""), updateData);

      toast.success("Company updated successfully!", {
        position: "top-right",
        autoClose: 2000,
      });

      fetchCustomer();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to update company";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="container-fluid p-4">
        <div className="alert alert-danger" role="alert">
          {error || "Customer not found"}
        </div>
        <Link to="/customers" className="btn btn-primary">
          <i className="bi bi-arrow-left me-2"></i>
          Back to Customers
        </Link>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <ToastContainer />

      <Breadcrumb
        items={[
          { label: 'Customers', path: '/customers' },
          { label: customer.name }
        ]}
      />

      {/* Tabs Navigation */}
      <div className="row">
        <div className="col-md-2">
          <div className="nav flex-column nav-pills sticky-top" style={{ top: '20px' }}>
            <button
              className={`nav-link text-start ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => handleTabChange('overview')}
            >
              <i className="bi bi-graph-up me-2"></i>
              Overview
            </button>
            <button
              className={`nav-link text-start ${activeTab === 'api-keys' ? 'active' : ''}`}
              onClick={() => handleTabChange('api-keys')}
            >
              <i className="bi bi-key me-2"></i>
              API Keys
            </button>
            <button
              className={`nav-link text-start ${activeTab === 'analytics' ? 'active' : ''}`}
              onClick={() => handleTabChange('analytics')}
            >
              <i className="bi bi-bar-chart me-2"></i>
              Analytics
            </button>
            <button
              className={`nav-link text-start ${activeTab === 'edit' ? 'active' : ''}`}
              onClick={() => handleTabChange('edit')}
            >
              <i className="bi bi-pencil me-2"></i>
              Edit Company
            </button>
          </div>
        </div>

        <div className="col-md-10">
          {activeTab === 'overview' && (
            <CustomerOverviewTab customer={customer} formData={formData} />
          )}

          {activeTab === 'api-keys' && (
            <CustomerApiKeysTab
              customer={customer}
              customerId={id || ""}
              searchTerm={searchTerm}
              filteredApiKeys={filteredApiKeys}
              sortField={sortField}
              sortDirection={sortDirection}
              togglingGemini={togglingGemini}
              onSearch={handleSearch}
              onSort={handleSort}
              onToggleGemini={handleToggleGemini}
              onCreateNew={handleCreateNew}
            />
          )}

          {activeTab === 'analytics' && (
            <CustomerAnalyticsTab customerId={parseInt(id || "0")} />
          )}

          {activeTab === 'edit' && (
            <CustomerEditTab
              formData={formData}
              formErrors={formErrors}
              onInputChange={handleInputChange}
              onSave={handleEditSave}
              isLoading={isEditLoading}
            />
          )}
        </div>
      </div>

      <ApiKeySidebar
        apiKeyId={selectedApiKeyId}
        customerId={parseInt(id || "")}
        isOpen={isSidebarOpen}
        onClose={() => handleCloseSidebar(true)}
        onSave={handleSaved}
      />
    </div>
  );
};

export default CustomerDetails;
